import { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Sun, Moon, Sparkles, ChevronRight, Github, Bell, Menu } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Tooltip } from '@/components/ui/Tooltip';
import { useApp } from '@/store/app';
import { findToolById } from '@/registry';
import { CATEGORY_BY_ID } from '@/registry/categories';

export function TopBar() {
  const { theme, toggleTheme, toggleAgent, togglePalette, toggleMobileSidebar } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const crumbs = useCrumbs(location.pathname);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-10 h-14 flex items-center justify-between gap-2 px-3 sm:px-6 border-b transition-all ${
        scrolled ? 'bg-bg/80 backdrop-blur-xl border-border' : 'bg-bg border-transparent'
      }`}
    >
      <div className="flex items-center gap-2 text-sm min-w-0 flex-1">
        {/* Mobile hamburger */}
        <button
          onClick={toggleMobileSidebar}
          className="lg:hidden size-8 -ml-1 rounded-md flex items-center justify-center text-fg-muted hover:text-fg hover:bg-surface-hover transition shrink-0"
          aria-label="Open menu"
        >
          <Menu size={18} />
        </button>

        <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
          {crumbs.map((c, i) => {
            /* On mobile, show only the current (last) crumb to save space */
            const isLast = i === crumbs.length - 1;
            return (
              <span
                key={i}
                className={`flex items-center gap-1.5 min-w-0 ${!isLast ? 'hidden sm:flex' : ''}`}
              >
                {i > 0 && <ChevronRight size={12} className="text-fg-subtle shrink-0" />}
                {c.to ? (
                  <Link to={c.to} className="text-fg-muted hover:text-fg transition-colors truncate">
                    {c.label}
                  </Link>
                ) : (
                  <span className="font-medium truncate">{c.label}</span>
                )}
              </span>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-1 shrink-0">
        <Tooltip content="Open agent">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={toggleAgent}
            className="text-orange-500 dark:text-amber-400 hover:text-orange-600 dark:hover:text-amber-300"
          >
            <Sparkles size={14} />
          </Button>
        </Tooltip>
        <Tooltip content="Notifications">
          <Button variant="ghost" size="icon-sm" className="hidden sm:inline-flex">
            <Bell size={14} />
          </Button>
        </Tooltip>
        <Tooltip content={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
          <Button variant="ghost" size="icon-sm" onClick={toggleTheme}>
            {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
          </Button>
        </Tooltip>
        <Tooltip content="Source">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => window.open('https://github.com/', '_blank')}
            className="hidden sm:inline-flex"
          >
            <Github size={14} />
          </Button>
        </Tooltip>
        <div className="w-px h-5 bg-border mx-1 hidden sm:block" />
        <Button variant="primary" size="sm" onClick={togglePalette}>
          <span className="hidden sm:inline">New task</span>
          <span className="sm:hidden">New</span>
          <kbd className="ml-1 bg-white/15 border-white/20 text-white hidden sm:inline-flex">⌘K</kbd>
        </Button>
        <button
          onClick={() => navigate('/settings')}
          className="ml-1 sm:ml-2 size-8 rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 hover:scale-110 hover:rotate-6 transition-transform border-2 border-fg/85 dark:border-fg/40 shadow-sticker-sm flex items-center justify-center text-white text-xs font-black [text-shadow:0_1px_0_rgba(0,0,0,0.25)] shrink-0"
          title="Account"
        >
          U
        </button>
      </div>
    </header>
  );
}

interface Crumb {
  label: string;
  to?: string;
}

function useCrumbs(path: string): Crumb[] {
  if (path === '/') return [{ label: 'Home' }];
  if (path === '/recent') return [{ label: 'Home', to: '/' }, { label: 'Recent' }];
  if (path === '/favorites') return [{ label: 'Home', to: '/' }, { label: 'Favorites' }];
  if (path === '/workflows') return [{ label: 'Home', to: '/' }, { label: 'Workflows' }];
  if (path === '/settings') return [{ label: 'Home', to: '/' }, { label: 'Settings' }];

  if (path.startsWith('/c/')) {
    const id = path.slice(3);
    const cat = CATEGORY_BY_ID[id];
    return [{ label: 'Home', to: '/' }, { label: cat?.name ?? id }];
  }
  if (path.startsWith('/tool/')) {
    const id = path.slice(6);
    const tool = findToolById(id);
    if (!tool) return [{ label: 'Home', to: '/' }, { label: 'Tool' }];
    const cat = CATEGORY_BY_ID[tool.category];
    return [
      { label: 'Home', to: '/' },
      { label: cat?.name ?? tool.category, to: `/c/${tool.category}` },
      { label: tool.name },
    ];
  }
  return [{ label: 'Home', to: '/' }];
}
